import {
    AuthorizePaymentCardResult,
    AvailabilityBlockFilterParameters,
    AvailabilityBlockResult,
    ConfigurationResult,
    CreateReservationGroupParameters,
    EnterpriseParameters,
    ExchangeRateParameters,
    ExchangeRateResult,
    GeneralServiceDocumentFilterParameters,
    GeneralServiceDocumentResult,
    PaymentCardFilterParameters,
    PaymentCardResult,
    PaymentConfiguration,
    ProductPricesResult,
    ProductPricingParameters,
    PromotedServiceResult,
    ReservationGroupParameters,
    ReservationGroupResult,
    ReservationPriceParameters,
    ReservationPricing,
    ReservationPricingParameters,
    ServiceAvailabilityParameters,
    ServiceAvailabilityResult,
    ServicePricingParameters,
    ServicePricingResult,
    ServicePromotionParameters,
} from '@mews-distributor/data-access';
import { CaseStyle, PlatformEnvironment } from '@mews/enums';
import { getBrowserInfo } from '@mews/framework';
import { sendRequest, SendRequestOptions } from '@mews/framework/data-access';
import { createUUID } from '@mews/std';
import type { AuthorizePaymentCardParameters } from '@mews-distributor/data-access';
import { createSession } from '../../utils/session/session';
import {
    AVAILABILITY_BLOCKS_GET_ALL,
    CONFIGURATION_GET,
    CONFIGURATIONS_GET,
    ENTERPRISES_GET_PAYMENT_CONFIGURATION,
    EXCHANGE_RATES_GET_ALL,
    PAYMENT_CARDS_AUTHORIZE,
    PRODUCTS_GET_PRICES,
    PROMOTED_SERVICES_GET,
    RESERVATION_GROUPS_CREATE,
    RESERVATION_GROUPS_GET,
    RESERVATIONS_GET_PRICING,
    RESERVATIONS_PRICE,
    SERVICE_DOCUMENTS_GET_ALL,
    SERVICES_GET_AVAILABILITY,
    SERVICES_GET_PRICING,
} from '../endpoints';
import { getRequestCredentials } from './credentials';

interface ApiServiceOptions {
    environment: PlatformEnvironment | string;
    getDistributorBaseUrl: () => string;
    getGeneralBaseUrl: () => string;
    getClientName: () => string;
    getSessionToken: () => string;
}
type RequestData = object | null;

export const createApiService = ({
    environment,
    getDistributorBaseUrl,
    getGeneralBaseUrl,
    getClientName,
    getSessionToken,
}: ApiServiceOptions) => {
    const request = async (
        baseUrl: string,
        apiUrl: string,
        data: RequestData = {},
        options: SendRequestOptions = {}
    ) => {
        const absoluteUrl = `${baseUrl}${apiUrl}`;
        const extendedData = appendMetadata(data);
        const correlationId = createUUID();

        try {
            return await sendRequest(
                absoluteUrl,
                {
                    credentials: getRequestCredentials(environment),
                    customHeaders: {
                        ...options.customHeaders,
                        'X-Accept-Casing': CaseStyle.Camel,
                        'X-Mews-CorrelationId': correlationId,
                    },
                    ...options,
                },
                extendedData
            );
        } catch (error) {
            error.correlationId = correlationId;
            throw error;
        }
    };

    const distributorRequest = async (apiUrl: string, data: RequestData, options?: SendRequestOptions) => {
        const baseUrl = getDistributorBaseUrl();
        return request(baseUrl, apiUrl, data, options);
    };

    const generalRequest = async (apiUrl: string, data: RequestData, options?: SendRequestOptions) => {
        const baseUrl = getGeneralBaseUrl();
        return request(baseUrl, apiUrl, data, options);
    };

    const appendMetadata = (data: RequestData) => {
        const client = getClientName();
        const sessionToken = getSessionToken();
        const session = createSession({ sessionToken, clientName: client });

        return { ...data, client, session };
    };
    interface GetConfigurationOptions {
        ids: string[];
        primaryId: string;
    }
    const getConfiguration = async ({ ids, primaryId }: GetConfigurationOptions) =>
        distributorRequest(CONFIGURATION_GET, { ids, primaryId });
    const getConfigurations = async ({ ids, primaryId }: GetConfigurationOptions): Promise<ConfigurationResult> =>
        distributorRequest(CONFIGURATIONS_GET, { ids, primaryId });

    const getServicesAvailability = async ({
        serviceId,
        bookingEngineId,
        availabilityBlockId,
        startUtc,
        endUtc,
        enterpriseId,
        fullAmounts,
        languageCode,
    }: ServiceAvailabilityParameters): Promise<ServiceAvailabilityResult> =>
        distributorRequest(SERVICES_GET_AVAILABILITY, {
            serviceId,
            bookingEngineId,
            availabilityBlockId,
            startUtc,
            endUtc,
            enterpriseId,
            fullAmounts,
            languageCode,
        });

    type GetServicesPricingParams = Omit<ServicePricingParameters, 'bookingEngineId'> & {
        configurationId?: string | null;
    };

    const getServicesPricing = async ({
        configurationId,
        ...params
    }: GetServicesPricingParams): Promise<ServicePricingResult> =>
        distributorRequest(SERVICES_GET_PRICING, {
            ...params,
            bookingEngineId: configurationId,
        });

    type GetReservationPricingParams = ReservationPricingParameters & {
        hotelId?: string;
        currencyCodes: string[];
    };

    const getReservationPricing = async ({
        currencyCodes,
        ...params
    }: GetReservationPricingParams): Promise<ReservationPricing> =>
        distributorRequest(RESERVATIONS_GET_PRICING, {
            currencyCodes,
            ...params,
        });

    const getReservationsPrice = async ({
        reservations,
        promotedServiceReservations,
        configurationId,
        languageCode,
        currencyCode,
        settlementCurrencyCode,
    }: ReservationPriceParameters) =>
        distributorRequest(RESERVATIONS_PRICE, {
            reservations,
            promotedServiceReservations,
            configurationId,
            languageCode,
            currencyCode,
            settlementCurrencyCode,
        });

    const createReservationGroup = async (params: CreateReservationGroupParameters) =>
        distributorRequest(RESERVATION_GROUPS_CREATE, params);

    const authorizePaymentCard = async ({
        enterpriseId,
        paymentCardId,
    }: Pick<AuthorizePaymentCardParameters, 'enterpriseId' | 'paymentCardId'>): Promise<AuthorizePaymentCardResult> =>
        distributorRequest(PAYMENT_CARDS_AUTHORIZE, {
            enterpriseId,
            paymentCardId,
            browserInfo: getBrowserInfo(),
        });

    const getPaymentConfiguration = async ({
        enterpriseId,
        languageCode,
    }: Pick<EnterpriseParameters, 'enterpriseId' | 'languageCode'>): Promise<PaymentConfiguration> =>
        distributorRequest(ENTERPRISES_GET_PAYMENT_CONFIGURATION, { enterpriseId, languageCode });

    const getProductsPrices = async (params: ProductPricingParameters): Promise<ProductPricesResult> =>
        distributorRequest(PRODUCTS_GET_PRICES, params);

    const getReservationGroup = async ({ hotelId, ids }: ReservationGroupParameters): Promise<ReservationGroupResult> =>
        distributorRequest(RESERVATION_GROUPS_GET, {
            hotelId,
            ids,
            extent: {
                paymentRequests: true,
                payments: true,
                resourceCategories: true,
            },
        });

    const getExchangeRates = async (params: ExchangeRateParameters): Promise<ExchangeRateResult> =>
        distributorRequest(EXCHANGE_RATES_GET_ALL, params);

    const getPaymentCards = async (params: PaymentCardFilterParameters): Promise<PaymentCardResult> =>
        distributorRequest('/paymentCards/getAll', params);
    const getAvailabilityBlocks = async (params: AvailabilityBlockFilterParameters): Promise<AvailabilityBlockResult> =>
        distributorRequest(AVAILABILITY_BLOCKS_GET_ALL, params);

    const getServiceDocuments = async (
        params: GeneralServiceDocumentFilterParameters
    ): Promise<GeneralServiceDocumentResult> =>
        generalRequest(SERVICE_DOCUMENTS_GET_ALL, {
            ...params,
            extent: {
                markdownValues: true,
            },
        });

    const getPromotedServices = async (params: ServicePromotionParameters): Promise<PromotedServiceResult> =>
        distributorRequest(PROMOTED_SERVICES_GET, params);

    return {
        authorizePaymentCard,
        createReservationGroup,
        getServicesAvailability,
        getAvailabilityBlocks,
        getConfiguration,
        getConfigurations,
        getExchangeRates,
        getPaymentCards,
        getPaymentConfiguration,
        getServicesPricing,
        getProductsPrices,
        getPromotedServices,
        getReservationGroup,
        getReservationPricing,
        getReservationsPrice,
        getServiceDocuments,
    };
};
